/**
 * LocationField — three-field input bundle: City/Address, Latitude, Longitude.
 * "Use my location" button reads navigator.geolocation when available.
 *
 * Pure controlled component — bubbles changes via onChange({location, latitude, longitude}).
 */
import { MapPin, Crosshair } from 'lucide-react';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const LocationField = ({
  location = '',
  latitude = '',
  longitude = '',
  onChange,
  testIdPrefix = 'asset',
  showLatLng = true,
  required = false,
}) => {
  const [locating, setLocating] = useState(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported in this browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange?.({
          location,
          latitude: Number(pos.coords.latitude.toFixed(6)),
          longitude: Number(pos.coords.longitude.toFixed(6)),
        });
        toast.success('Location captured');
        setLocating(false);
      },
      (err) => {
        toast.error(err.message || 'Could not get location');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleField = (field) => (e) => {
    const v = e.target.value;
    onChange?.({
      location,
      latitude,
      longitude,
      [field]: field === 'location' ? v : (v === '' ? '' : Number(v)),
    });
  };

  const mapHref = (latitude && longitude)
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : null;

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-location-field`}>
      <div>
        <Label className="text-text-primary flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gold" /> Location {required && <span className="text-red-400">*</span>}
        </Label>
        <Input
          value={location || ''}
          onChange={handleField('location')}
          placeholder="e.g. Dubai Marina, Mumbai"
          className="bg-white/5 border-white/15 text-text-primary mt-1"
          data-testid={`${testIdPrefix}-location-input`}
          required={required}
        />
      </div>

      {showLatLng && (
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-text-secondary text-xs">Latitude</Label>
            <Input
              type="number"
              step="any"
              value={latitude ?? ''}
              onChange={handleField('latitude')}
              placeholder="25.0792"
              className="bg-white/5 border-white/15 text-text-primary mt-1"
              data-testid={`${testIdPrefix}-latitude-input`}
            />
          </div>
          <div>
            <Label className="text-text-secondary text-xs">Longitude</Label>
            <Input
              type="number"
              step="any"
              value={longitude ?? ''}
              onChange={handleField('longitude')}
              placeholder="55.1378"
              className="bg-white/5 border-white/15 text-text-primary mt-1"
              data-testid={`${testIdPrefix}-longitude-input`}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={locating}
            onClick={useMyLocation}
            className="border-gold/30 text-gold hover:bg-gold/10 col-span-2 sm:col-span-1"
            data-testid={`${testIdPrefix}-use-my-location-btn`}
          >
            <Crosshair className={`w-4 h-4 mr-1 ${locating ? 'animate-pulse' : ''}`} />
            {locating ? 'Getting…' : 'Use my location'}
          </Button>
        </div>
      )}

      {mapHref && (
        <a
          href={mapHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gold/80 hover:text-gold underline"
          data-testid={`${testIdPrefix}-map-preview-link`}
        >
          Preview on Google Maps →
        </a>
      )}
    </div>
  );
};

export default LocationField;
